import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const AuthContext = createContext();

// Get admin customer list from environment variables
const getAdminCustomerList = () => {
  const envList = process.env.REACT_APP_ADMIN_CUSTOMER_LIST;
  // Parse the environment variable - should be a comma-separated list without quotes
  return envList
    ? envList.split(",").map((num) => num.trim())
    : ["10513", "10614", "10615", "10612"];
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(() => {
    const token = localStorage.getItem("accessToken");
    const userProfile = localStorage.getItem("userProfile");
    return token
      ? {
          accessToken: token,
          profile: userProfile ? JSON.parse(userProfile) : null,
        }
      : null;
  });
  const [isLoading, setIsLoading] = useState(true);
  const [customerNumbers, setCustomerNumbers] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem("accessToken");
    if (token) {
      axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
      const userProfile = JSON.parse(localStorage.getItem("userProfile"));

      // Determine customer numbers based on user type
      const customerNums = determineUserCustomerNumbers(userProfile);

      setCustomerNumbers(customerNums);
      setCurrentUser({ accessToken: token, profile: userProfile });
    }
    setIsLoading(false);
  }, []);

  // Helper function to determine customer numbers based on user type
  const determineUserCustomerNumbers = (userProfile) => {
    // If user is admin, use the admin customer list from environment variables
    if (userProfile?.isAdmin) {
      const adminCustomerList = getAdminCustomerList();
      console.log(
        "Admin user - using customer list from env:",
        adminCustomerList
      );
      return adminCustomerList;
    }

    // Otherwise, if user is a customer, use their assigned customer numbers
    if (
      userProfile?.perfil?.isCustomer &&
      userProfile?.perfil?.customerNumber
    ) {
      return userProfile.perfil.customerNumber;
    }

    // Default to empty array if neither admin nor customer with assigned numbers
    return [];
  };

  const login = async (email, password, captchaValue, onLoggedIn) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_API_URL}/api/auth/login`,
        {
          email,
          password,
          captchaValue,
        }
      );

      if (response.data && response.data.accessToken) {
        // For admin users, skip the BUCKLAND CONNECT system access check
        const isAdmin = response.data.isAdmin === true;

        // Check if user has access to BUCKLAND CONNECT (skip for admins)
        const hasBucklandAccess =
          isAdmin ||
          response.data.sistemas?.some(
            (system) => system.id === 5 && system.value === "ECHO XB CONNECT"
          );

        if (!hasBucklandAccess) {
          onLoggedIn(new Error("You do not have access to this system"));
          return;
        }

        // Determine customer numbers based on user type
        const customerNums = determineUserCustomerNumbers(response.data);
        setCustomerNumbers(customerNums);

        // Store data in localStorage
        localStorage.setItem("accessToken", response.data.accessToken);
        localStorage.setItem("userProfile", JSON.stringify(response.data));

        // Set Authorization header
        axios.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${response.data.accessToken}`;

        // Update current user state
        setCurrentUser({
          accessToken: response.data.accessToken,
          profile: response.data,
        });

        onLoggedIn(null); // No error, login successful
      } else {
        onLoggedIn(new Error("Login failed")); // Login failed
      }
    } catch (error) {
      console.error("Login error:", error);
      onLoggedIn(error); // Pass error to callback if needed
    }
  };

  const logout = () => {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("userProfile");
    delete axios.defaults.headers.common["Authorization"];
    setCurrentUser(null);
    setCustomerNumbers([]);
  };

  const value = {
    currentUser,
    isLoading,
    login,
    logout,
    customerNumbers,
    isAdmin: currentUser?.profile?.isAdmin || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
